// ===========================================
// KpiCard — Carte indicateur clé du dashboard
// Icône + label + valeur + delta + sous-titre, cliquable (navigate).
// ===========================================
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from '@ant-design/icons';
import { formatMontant } from '../../../common/utils/format';

const DEFAULT_TONE = 'blue';

const TONE_STYLES = {
    blue:   { color: '#1A2B5F', bg: 'rgba(26, 43, 95, 0.10)' },
    red:    { color: '#C8102E', bg: 'rgba(200, 16, 46, 0.10)' },
    green:  { color: '#16a34a', bg: 'rgba(22, 163, 74, 0.10)' },
    orange: { color: '#ea580c', bg: 'rgba(234, 88, 12, 0.10)' },
    purple: { color: '#7c3aed', bg: 'rgba(124, 58, 237, 0.10)' },
    grey:   { color: '#475569', bg: 'rgba(71, 85, 105, 0.10)' },
    teal:   { color: '#0891b2', bg: 'rgba(8, 145, 178, 0.10)' },
    indigo: { color: '#4f46e5', bg: 'rgba(79, 70, 229, 0.10)' },
};

const DeltaBadge = ({ value, prefix = '' }) => {
    if (value === null || value === undefined) return null;
    const isPositive = value > 0;
    const isNegative = value < 0;

    const cls = isPositive ? 'kpi-delta-up' : isNegative ? 'kpi-delta-down' : 'kpi-delta-neutral';
    const Icon = isPositive ? ArrowUpOutlined : isNegative ? ArrowDownOutlined : MinusOutlined;
    const sign = isPositive ? '+' : '';

    return (
        <span className={`kpi-delta ${cls}`}>
            <Icon />
            <span>{prefix}{sign}{value}%</span>
        </span>
    );
};

const KpiCard = ({
    label,
    value,
    formatted,             // si fourni, override le formatage automatique de value
    isCurrency = false,    // formate la valeur en FCFA
    icon,
    tone = DEFAULT_TONE,
    delta,                 // pourcentage vs N-1
    deltaSuffix,           // ex: "vs mois dernier"
    subtitle,              // sous-titre contextuel
    loading = false,
    to,                    // route à naviguer au clic
    badge,                 // ex: 6 (impayés urgents) - tag rouge en haut à droite
}) => {
    const navigate = useNavigate();
    const tones = TONE_STYLES[tone] || TONE_STYLES[DEFAULT_TONE];

    const handleClick = () => {
        if (to) navigate(to);
    };

    const displayValue = formatted !== undefined
        ? formatted
        : (isCurrency ? `${formatMontant(value || 0)} F` : (value ?? 0).toLocaleString('fr-FR'));

    if (loading) {
        return (
            <div className="kpi-card kpi-card-loading" aria-busy="true">
                <Skeleton active title={false} paragraph={{ rows: 3, width: ['40%', '70%', '50%'] }} />
            </div>
        );
    }

    return (
        <button
            type="button"
            className={`kpi-card kpi-card-${tone} ${to ? 'kpi-card-clickable' : ''}`}
            onClick={handleClick}
            aria-label={`${label}: ${displayValue}`}
            tabIndex={to ? 0 : -1}
        >
            <div className="kpi-card-header">
                <div
                    className="kpi-card-icon"
                    style={{ background: tones.bg, color: tones.color }}
                >
                    {icon}
                </div>
                <div className="kpi-card-label">{label}</div>
                {badge !== undefined && badge > 0 && (
                    <div className="kpi-card-badge">{badge}</div>
                )}
            </div>

            <div className="kpi-card-value">{displayValue}</div>

            <div className="kpi-card-footer">
                {delta !== undefined && delta !== null && <DeltaBadge value={delta} />}
                {subtitle && <span className="kpi-card-subtitle">{subtitle}</span>}
                {!subtitle && deltaSuffix && delta !== undefined && (
                    <span className="kpi-card-subtitle">{deltaSuffix}</span>
                )}
            </div>
        </button>
    );
};

export default KpiCard;
